import { z } from 'zod';

export const CITATION_SECTIONS = ['assessment', 'override', 'advancement', 'workflow', 'family'] as const;
export type CitationSection = (typeof CITATION_SECTIONS)[number];

export const CITATION_KINDS = ['applicant', 'recommendation', 'activity', ...CITATION_SECTIONS] as const;
export const CitationKind = z.enum(CITATION_KINDS);
export type CitationKind = z.infer<typeof CitationKind>;

export const CITATION_SECTION_LABELS: Record<CitationSection, string> = {
  assessment: 'Assessment',
  override: 'Override',
  advancement: 'Advancement',
  workflow: 'Workflow',
  family: 'Family',
};

export interface CitationTag {
  tag: string;
  kind: CitationKind;
  date: string | null;
}

export type CitedSegment = { type: 'text'; text: string } | { type: 'tags'; raw: string; tags: CitationTag[] };

const GROUP = /\[([^[\]\n]{1,120})\]/g;
const REF = /^([AR])0*(\d{1,4})$/i;
const ACTIVITY = /^activity\s+(\d{4}-\d{2}-\d{2})$/i;
const SECTION_BY_NAME = new Map<string, CitationSection>(CITATION_SECTIONS.map((s) => [s, s]));

function isCalendarDate(date: string): boolean {
  const d = new Date(`${date}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === date;
}

export function parseCitationTag(raw: string): CitationTag | null {
  const part = raw.trim().replace(/\s+/g, ' ');
  const ref = REF.exec(part);
  if (ref) {
    const letter = ref[1]!.toUpperCase();
    return { tag: `${letter}${Number(ref[2])}`, kind: letter === 'A' ? 'applicant' : 'recommendation', date: null };
  }
  const activity = ACTIVITY.exec(part);
  if (activity) return isCalendarDate(activity[1]!) ? { tag: `Activity ${activity[1]}`, kind: 'activity', date: activity[1]! } : null;
  const section = SECTION_BY_NAME.get(part.toLowerCase());
  return section ? { tag: CITATION_SECTION_LABELS[section], kind: section, date: null } : null;
}

function parseGroup(inner: string): CitationTag[] | null {
  const parts = inner.split(/[,;]/);
  const tags = parts.map(parseCitationTag);
  return tags.every((t): t is CitationTag => t !== null) ? tags : null;
}

export function splitCitedText(text: string): CitedSegment[] {
  const segments: CitedSegment[] = [];
  let last = 0;
  for (const m of text.matchAll(GROUP)) {
    const tags = parseGroup(m[1]!);
    if (!tags) continue;
    if (m.index > last) segments.push({ type: 'text', text: text.slice(last, m.index) });
    segments.push({ type: 'tags', raw: m[0], tags });
    last = m.index + m[0].length;
  }
  if (last < text.length) segments.push({ type: 'text', text: text.slice(last) });
  return segments;
}

export function citationTags(text: string): CitationTag[] {
  const seen = new Map<string, CitationTag>();
  for (const s of splitCitedText(text)) {
    if (s.type === 'tags') for (const t of s.tags) if (!seen.has(t.tag)) seen.set(t.tag, t);
  }
  return [...seen.values()];
}
